/**
 * The customer's Earthworks quote, opened from the emailed link with no login
 * (EW-2, fieldiq-api `GET /quote-review/:token`). Like QuickBooks: read the
 * quote, download the PDF, then Accept (typed name, optional title, signature)
 * or Decline (name, optional reason).
 *
 * PUBLIC. Every request goes through `publicApiFetch` (api/public.ts), which
 * never sends a staff token — see TicketReviewPage for why — and the path is in
 * lib/public-routes.ts so a stale staff session cannot bounce the reader to
 * /login.
 *
 * The API decides everything: the numbers are the quote's own strings, the
 * status is the link's. 404 is "this link is not valid", 410 is "no longer
 * open", 409 is "already decided" — three screens, not one error string.
 *
 * PAY DEPOSIT BY CARD (Batch 4b). The button shows only when the API says
 * `deposit.canPayByCard` (accepted, not received, and the tenant's own Stripe
 * can confirm a payment). It asks POST /quote-review/:token/deposit-checkout for
 * a Stripe Checkout url and goes there. Coming back with ?deposit=success does
 * NOT mean received — only the signed webhook records it — so the page says
 * the payment is being confirmed until the API reports `received`.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { AlertTriangle, CheckCircle, CreditCard, Download, FileText, Loader2, XCircle } from 'lucide-react';
import { API_URL } from '../api/client';
import { publicApiFetch, getPublicFetchMessage } from '../api/public';
import SignaturePad from '../components/SignaturePad';
import { fmtCurrency } from '../lib/display-labels';
import { formatCalendarDate, formatInstant, formatInstantDay } from '../lib/calendar-date';
import type { EwQuoteReview } from '../types/earthworks';

type Gone = { kind: 'invalid' | 'closed'; message: string };

const INPUT = 'w-full border rounded-lg px-3 py-2 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-amber-500';
const day = (d: string | null | undefined) => formatCalendarDate(d ?? null) ?? '—';

export default function QuoteReviewPage() {
  const { token } = useParams<{ token: string }>();
  const [quote, setQuote] = useState<EwQuoteReview | null>(null);
  const [loading, setLoading] = useState(true);
  const [gone, setGone] = useState<Gone | null>(null);
  const [mode, setMode] = useState<'accept' | 'decline' | null>(null);
  const [name, setName] = useState('');
  const [title, setTitle] = useState('');
  const [reason, setReason] = useState('');
  const [signature, setSignature] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchParams] = useSearchParams();
  const returned = searchParams.get('deposit'); // 'success' | 'cancelled' | null — Stripe's redirect back
  const [paying, setPaying] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await publicApiFetch(`/quote-review/${token}`);
      const body = await res.json().catch(() => ({}));
      if (res.status === 404) setGone({ kind: 'invalid', message: 'This quote link is not valid. Please ask for a new one.' });
      else if (res.status === 410) setGone({ kind: 'closed', message: body?.error || 'This quote is no longer open. Please ask for a new one.' });
      else if (!res.ok) setError(body?.error || 'The quote could not be loaded. Please try again.');
      else setQuote(body.data as EwQuoteReview);
    } catch (err) {
      setError(getPublicFetchMessage(err, 'Unable to connect. Please try again.'));
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { void load(); }, [load]);

  const decide = async (kind: 'accept' | 'decline') => {
    setError(null);
    if (!name.trim()) { setError(kind === 'accept' ? 'Type your name to accept the quote.' : 'Type your name to decline the quote.'); return; }
    if (kind === 'accept' && !signature) { setError('Sign in the box, then press Confirm signature.'); return; }
    setSubmitting(true);
    try {
      const body = kind === 'accept'
        ? { name: name.trim(), title: title.trim() || undefined, signature }
        : { name: name.trim(), reason: reason.trim() || undefined };
      const res = await publicApiFetch(`/quote-review/${token}/${kind}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const out = await res.json().catch(() => ({}));
      if (res.ok || res.status === 409) {
        // 409: decided already (here or elsewhere) — reload shows the decision.
        setMode(null);
        await load();
        if (res.status === 409) setError(out?.error || 'This quote has already been decided.');
      } else if (res.status === 410) {
        setGone({ kind: 'closed', message: out?.error || 'This quote is no longer open. Please ask for a new one.' });
      } else {
        setError(out?.error || 'That did not go through. Please try again.');
      }
    } catch (err) {
      setError(getPublicFetchMessage(err, 'Unable to connect. Please try again.'));
    } finally {
      setSubmitting(false);
    }
  };

  const payDeposit = async () => {
    setError(null);
    setPaying(true);
    try {
      const res = await publicApiFetch(`/quote-review/${token}/deposit-checkout`, { method: 'POST' });
      const out = await res.json().catch(() => ({}));
      if (res.ok && out?.data?.url) {
        window.location.assign(out.data.url);
        return; // leaving the page; keep the spinner
      }
      if (res.status === 409) await load(); // received meanwhile, or no longer payable: show what is true now
      setError(out?.error || 'The card payment page could not be opened. Please try again.');
    } catch (err) {
      setError(getPublicFetchMessage(err, 'Unable to connect. Please try again.'));
    }
    setPaying(false);
  };

  if (loading && !quote) {
    return (
      <div className="min-h-full bg-gray-50 flex items-center justify-center" data-testid="quote-review-loading">
        <Loader2 className="w-8 h-8 text-amber-600 animate-spin" />
      </div>
    );
  }

  if (gone || (!quote && error)) {
    return (
      <div className="min-h-full bg-gray-50">
        <div className="max-w-3xl mx-auto px-6 py-12">
          <div className="bg-white rounded-xl shadow-sm border p-8 text-center" data-testid="quote-review-gone">
            <AlertTriangle className="w-14 h-14 text-amber-500 mx-auto mb-4" />
            <h1 className="text-xl font-bold text-gray-900 mb-2">
              {gone?.kind === 'closed' ? 'This quote is no longer open' : gone ? 'Link not valid' : 'Something went wrong'}
            </h1>
            <p className="text-gray-600">{gone?.message ?? error}</p>
          </div>
        </div>
      </div>
    );
  }
  if (!quote) return null;

  const cur = quote.currency === 'CAD' || quote.currency === 'USD' ? quote.currency : null;
  const money = (v: string | null | undefined) => fmtCurrency(v, cur);
  const pdfUrl = `${API_URL}/quote-review/${token}/pdf`;
  const decided = quote.decision;
  const accent = quote.company.primaryColor || '#1e293b';

  return (
    <div className="min-h-full bg-gray-50" data-testid="quote-review-page">
      <div className="bg-white border-b px-6 py-4" style={{ borderTop: `4px solid ${accent}` }}>
        <div className="max-w-5xl mx-auto flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            {quote.company.logoUrl && <img src={quote.company.logoUrl} alt="" className="h-9" />}
            <div>
              <h1 className="font-bold text-gray-900">{quote.company.name}</h1>
              <p className="text-sm text-gray-500">Quote {quote.quoteNumber}</p>
            </div>
          </div>
          <a href={pdfUrl} target="_blank" rel="noreferrer" data-testid="quote-review-pdf"
             className="inline-flex items-center gap-2 border border-gray-300 rounded-lg px-3 py-2 text-sm text-gray-700 hover:bg-gray-50">
            <Download className="w-4 h-4" /> Download PDF
          </a>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-6 py-6 space-y-4">
        {decided && (
          <div data-testid="quote-review-decided"
               className={`rounded-xl border p-5 flex items-start gap-3 ${decided.decision === 'accepted' ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}`}>
            {decided.decision === 'accepted'
              ? <CheckCircle className="w-6 h-6 text-green-600 shrink-0" />
              : <XCircle className="w-6 h-6 text-red-600 shrink-0" />}
            <div>
              <p className="font-semibold text-gray-900">
                {decided.decision === 'accepted' ? 'Quote accepted' : 'Quote declined'}
                {decided.signerName ? ` by ${decided.signerName}` : ''}
                {decided.signerTitle ? `, ${decided.signerTitle}` : ''}
              </p>
              <p className="text-sm text-gray-600">
                {formatInstant(decided.decidedAt, quote.company.timeZone) ?? ''}
                {decided.declineReason ? ` — “${decided.declineReason}”` : ''}
              </p>
              <p className="text-sm text-gray-600 mt-1">
                {decided.decision === 'accepted'
                  ? `Thank you. ${quote.company.name ?? 'The company'} has your acceptance and will be in touch to schedule the work.`
                  : `Thank you for letting ${quote.company.name ?? 'the company'} know.`}
              </p>
            </div>
          </div>
        )}

        <div className="bg-white rounded-xl shadow-sm border p-5">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
            <div><p className="text-gray-500">Prepared for</p><p className="font-semibold text-gray-900">{quote.customer.name ?? '—'}</p>
              {quote.customer.contactName && <p className="text-gray-600">{quote.customer.contactName}</p>}</div>
            <div><p className="text-gray-500">Quote date</p><p className="font-semibold text-gray-900">{formatInstantDay(quote.quoteDate, quote.company.timeZone) ?? '—'}</p></div>
            <div><p className="text-gray-500">Valid until</p><p className="font-semibold text-gray-900">{day(quote.validUntil)}</p></div>
            <div><p className="text-gray-500">Total</p><p className="font-bold text-lg text-gray-900" data-testid="quote-review-total">{money(quote.total)}</p></div>
          </div>
        </div>

        {/* Lines. A phone gets one card per line, the amount always in view; a
            wider screen gets the table. The totals sit below both, once, so a
            customer never scrolls sideways to find what they are agreeing to. */}
        <div className="bg-white rounded-xl shadow-sm border">
          <ul className="sm:hidden divide-y" data-testid="quote-review-lines-compact">
            {quote.lines.map((l, i) => (
              <li key={i} className="px-4 py-3 text-sm">
                <div className="flex items-start justify-between gap-3">
                  <span className="text-gray-900 min-w-0 break-words">{l.description}</span>
                  <span className="font-medium text-gray-900 whitespace-nowrap">{money(l.total)}</span>
                </div>
                {!l.isDiscount && (
                  <p className="mt-0.5 text-xs text-gray-500">
                    {l.quantity ?? '—'} × {money(l.rate)} · {l.unit || '—'}
                  </p>
                )}
              </li>
            ))}
          </ul>
          <div className="hidden sm:block overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-gray-500 text-left">
                <tr>
                  <th className="px-4 py-2 font-medium">Description</th>
                  <th className="px-4 py-2 font-medium">Unit</th>
                  <th className="px-4 py-2 font-medium text-right">Qty</th>
                  <th className="px-4 py-2 font-medium text-right">Rate</th>
                  <th className="px-4 py-2 font-medium text-right">Amount</th>
                </tr>
              </thead>
              <tbody>
                {quote.lines.map((l, i) => (
                  <tr key={i} className="border-t">
                    <td className="px-4 py-2 text-gray-900">{l.description}</td>
                    <td className="px-4 py-2 text-gray-600">{l.isDiscount ? '' : l.unit || '—'}</td>
                    <td className="px-4 py-2 text-right text-gray-900">{l.isDiscount ? '' : l.quantity ?? '—'}</td>
                    <td className="px-4 py-2 text-right text-gray-900">{l.isDiscount ? '' : money(l.rate)}</td>
                    <td className="px-4 py-2 text-right font-medium text-gray-900">{money(l.total)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <dl className="border-t px-4 py-3 text-sm text-gray-900 space-y-1 sm:ml-auto sm:max-w-xs" data-testid="quote-review-totals">
            <div className="flex justify-between gap-4"><dt className="text-gray-500">Subtotal</dt><dd>{money(quote.subtotal)}</dd></div>
            {quote.taxes.map((t) => (
              <div key={t.label} className="flex justify-between gap-4">
                <dt className="text-gray-500">{t.label}{t.percent ? ` (${Number(t.percent)}%)` : ''}</dt><dd>{money(t.amount)}</dd>
              </div>
            ))}
            <div className="flex justify-between gap-4 font-bold"><dt>Total</dt><dd>{money(quote.total)}</dd></div>
          </dl>
        </div>

        {quote.deposit && (
          <div className="bg-white rounded-xl shadow-sm border p-5 text-sm text-gray-700 space-y-3" data-testid="quote-review-deposit">
            {quote.deposit.received ? (
              <p className="flex items-start gap-2" data-testid="quote-review-deposit-received">
                <CheckCircle className="w-5 h-5 text-green-600 shrink-0" />
                <span>
                  <span className="font-semibold text-gray-900">Deposit received: </span>
                  {money(quote.deposit.amount)}
                  {quote.deposit.paidByCard ? ' paid by card' : ''}
                  {quote.deposit.receivedAt ? ` on ${formatInstantDay(quote.deposit.receivedAt, quote.company.timeZone) ?? '—'}` : ''}.
                  {' '}Thank you.
                </span>
              </p>
            ) : (
              <p>
                <span className="font-semibold text-gray-900">Deposit: </span>
                {money(quote.deposit.amount)}
                {quote.deposit.basis === 'percent' && quote.deposit.percent ? ` (${Number(quote.deposit.percent)}% of the total)` : ''}
                {' '}is asked for on acceptance.
                {quote.deposit.canPayByCard
                  ? ' You can pay it by card below, or as the company tells you.'
                  : ` ${quote.company.name ?? 'The company'} will tell you how to pay it.`}
              </p>
            )}
            {!quote.deposit.received && returned === 'success' && (
              <p className="rounded-lg bg-amber-50 border border-amber-200 p-3 text-amber-900" data-testid="quote-review-deposit-confirming">
                Thank you. Your card payment is being confirmed — this page will show the deposit as received once it is.
              </p>
            )}
            {!quote.deposit.received && returned === 'cancelled' && (
              <p className="text-gray-600" data-testid="quote-review-deposit-cancelled">The card payment was cancelled. Nothing was charged.</p>
            )}
            {!quote.deposit.received && quote.deposit.canPayByCard && (
              <button type="button" disabled={paying} onClick={payDeposit} data-testid="quote-review-pay-deposit"
                      className="inline-flex items-center gap-2 bg-green-600 text-white rounded-lg px-5 py-2.5 font-semibold hover:bg-green-700 disabled:opacity-60">
                {paying ? <Loader2 className="w-5 h-5 animate-spin" /> : <CreditCard className="w-5 h-5" />}
                Pay deposit {money(quote.deposit.amount)}
              </button>
            )}
          </div>
        )}

        {quote.notes && (
          <div className="bg-white rounded-xl shadow-sm border p-5 text-sm text-gray-700 whitespace-pre-line">
            <FileText className="w-4 h-4 inline mr-1 text-gray-400" />{quote.notes}
          </div>
        )}

        {error && (
          <div className="bg-red-50 border border-red-200 rounded-xl p-3 text-red-700 text-sm" role="alert">{error}</div>
        )}

        {!decided && quote.status === 'open' && (
          <div className="bg-white rounded-xl shadow-sm border p-5 space-y-4" data-testid="quote-review-decide">
            {mode === null && (
              <div className="flex flex-wrap gap-3">
                <button type="button" onClick={() => { setMode('accept'); setError(null); }}
                        className="inline-flex items-center gap-2 bg-green-600 text-white rounded-lg px-5 py-2.5 font-semibold hover:bg-green-700">
                  <CheckCircle className="w-5 h-5" /> Accept quote
                </button>
                <button type="button" onClick={() => { setMode('decline'); setError(null); }}
                        className="inline-flex items-center gap-2 border border-gray-300 text-gray-700 rounded-lg px-5 py-2.5 font-semibold hover:bg-gray-50">
                  <XCircle className="w-5 h-5" /> Decline
                </button>
              </div>
            )}

            {mode && (
              <>
                <h2 className="font-bold text-gray-900">{mode === 'accept' ? 'Accept this quote' : 'Decline this quote'}</h2>
                <div className="grid gap-3 sm:grid-cols-2">
                  <div>
                    <label htmlFor="qr-name" className="block text-xs text-gray-500 mb-1">Your name</label>
                    <input id="qr-name" className={INPUT} value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. John Smith" />
                  </div>
                  {mode === 'accept' ? (
                    <div>
                      <label htmlFor="qr-title" className="block text-xs text-gray-500 mb-1">Title (optional)</label>
                      <input id="qr-title" className={INPUT} value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. Owner" />
                    </div>
                  ) : (
                    <div>
                      <label htmlFor="qr-reason" className="block text-xs text-gray-500 mb-1">Reason (optional)</label>
                      <input id="qr-reason" className={INPUT} value={reason} onChange={(e) => setReason(e.target.value)} />
                    </div>
                  )}
                </div>
                {mode === 'accept' && (
                  <SignaturePad tone="document" label="Signature" value={signature ?? undefined}
                                onSave={setSignature} onClear={() => setSignature(null)} />
                )}
                {mode === 'accept' && (
                  <p className="text-xs text-gray-500">
                    By signing you accept quote {quote.quoteNumber} for {money(quote.total)}, on its terms.
                  </p>
                )}
                <div className="flex flex-wrap gap-3">
                  <button type="button" disabled={submitting} onClick={() => decide(mode)}
                          className={`inline-flex items-center gap-2 rounded-lg px-5 py-2.5 font-semibold text-white disabled:opacity-60 ${mode === 'accept' ? 'bg-green-600 hover:bg-green-700' : 'bg-red-600 hover:bg-red-700'}`}>
                    {submitting && <Loader2 className="w-4 h-4 animate-spin" />}
                    {mode === 'accept' ? 'Sign and accept' : 'Decline quote'}
                  </button>
                  <button type="button" disabled={submitting} onClick={() => { setMode(null); setError(null); }}
                          className="border border-gray-300 text-gray-700 rounded-lg px-5 py-2.5 font-semibold hover:bg-gray-50">
                    Back
                  </button>
                </div>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
